import { parseEmail, parsePassword } from '@d2/schema/auth';
import { isCharacterClass, parseCharacterName } from '@d2/schema/characters';

import { type ApiRefusal } from './api';

/**
 * What a form refuses before anything is sent, and how a refusal is put into
 * words.
 *
 * ## Every check is the API's own parser, not a copy of it
 *
 * `parseEmail`, `parsePassword` and `parseCharacterName` are the *same*
 * functions `apps/api` validates with — they live in `@d2/schema` for exactly
 * this reason, so a form and a controller cannot drift about what is
 * acceptable. Nothing here restates a length, a character class or a pattern.
 *
 * The one rule this file therefore does **not** check is the display name:
 * `parseDisplayName` is private to `apps/api/src/auth/requests.ts` and there is
 * no shared definition to reuse. Copying its whitelist here would be a second
 * definition of it, so the form only asks that something was typed and lets the
 * API's `{field, reason}` say the rest. A rule with one home and a slightly
 * later error beats a rule with two homes.
 *
 * ## And the sign-in form deliberately checks almost nothing
 *
 * `apps/api`'s `parseLoginBody` does not hold a login's password to the password
 * policy, and says why: *"A login must not tell a caller that the password they
 * typed is too short to be one of ours — that is a fact about the password
 * policy, and repeating it on the login form narrows the search space for
 * everyone else."* A client-side check would give away the same fact one step
 * earlier and without a request. So the sign-in form asks only that both boxes
 * have something in them.
 *
 * ## Nothing here ever holds a value
 *
 * A `FieldMessage` names a field and says what is wrong with it. It never
 * carries what was typed, because these messages are rendered, and a password
 * rendered is a password on somebody's screen.
 */

/** One thing wrong with one field. Never the value — see above. */
export interface FieldMessage {
  readonly field: string;
  readonly message: string;
}

export interface RegistrationForm {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
}

export interface SignInForm {
  readonly email: string;
  readonly password: string;
}

export interface NewCharacterForm {
  readonly name: string;
  readonly characterClass: string;
}

/** What each refusal reason means, for whichever field carried it. */
const REASONS: Readonly<Record<string, string>> = {
  malformed: 'That is not something we can accept here.',
  'too-short': 'That is too short.',
  'too-long': 'That is too long.',
  'unknown-class': 'Choose one of the three classes.',
  'unexpected-fields': 'This form sent something the game did not expect. Reload the page and try again.',
  taken: 'That is already taken.',
  unavailable: 'That is already taken.',
};

const EMAIL_MESSAGES: Readonly<Record<string, string>> = {
  malformed: 'That does not look like an email address.',
  'too-long': 'That email address is too long.',
};

const PASSWORD_MESSAGES: Readonly<Record<string, string>> = {
  malformed: 'A password cannot contain control characters.',
  'too-short': 'A password must be at least 12 characters. Length is the only rule — a passphrase is ideal.',
  'too-long': 'That password is too long.',
};

const NAME_MESSAGES: Readonly<Record<string, string>> = {
  malformed: 'Letters, digits, hyphens and underscores only, and not starting or ending with one.',
  'too-short': 'A name needs at least two characters.',
  'too-long': 'That name is too long.',
};

const message = (table: Readonly<Record<string, string>>, reason: string, fallback: string): string =>
  table[reason] ?? fallback;

export const checkRegistration = ({ email, password, displayName }: RegistrationForm): readonly FieldMessage[] => {
  const messages: FieldMessage[] = [];

  const parsedEmail = parseEmail(email);
  if (!parsedEmail.ok) {
    messages.push({ field: 'email', message: message(EMAIL_MESSAGES, parsedEmail.reason, 'Check this email address.') });
  }

  const parsedPassword = parsePassword(password);
  if (!parsedPassword.ok) {
    messages.push({ field: 'password', message: message(PASSWORD_MESSAGES, parsedPassword.reason, 'Check this password.') });
  }

  // The whole of what this form knows about a display name. See the header.
  if (displayName.trim().length === 0) {
    messages.push({ field: 'displayName', message: 'Choose a name to be known by.' });
  }

  return messages;
};

export const checkSignIn = ({ email, password }: SignInForm): readonly FieldMessage[] => {
  const messages: FieldMessage[] = [];

  if (email.trim().length === 0) messages.push({ field: 'email', message: 'Enter the email address you signed up with.' });
  if (password.length === 0) messages.push({ field: 'password', message: 'Enter your password.' });

  return messages;
};

export const checkNewCharacter = ({ name, characterClass }: NewCharacterForm): readonly FieldMessage[] => {
  const messages: FieldMessage[] = [];

  const parsedName = parseCharacterName(name);
  if (!parsedName.ok) {
    messages.push({ field: 'name', message: message(NAME_MESSAGES, parsedName.reason, 'Check this name.') });
  }

  if (!isCharacterClass(characterClass)) {
    messages.push({ field: 'characterClass', message: 'Choose a class.' });
  }

  return messages;
};

/** The message for one field, or `null` when that field is fine. */
export const messageFor = (messages: readonly FieldMessage[], field: string): string | null =>
  messages.find((entry) => entry.field === field)?.message ?? null;

/** Which table of prose a field's reason is read out of. */
const tableFor = (field: string): Readonly<Record<string, string>> => {
  if (field === 'email') return EMAIL_MESSAGES;
  if (field === 'password' || field === 'newPassword' || field === 'currentPassword') return PASSWORD_MESSAGES;
  if (field === 'name') return NAME_MESSAGES;

  return REASONS;
};

export const describeRefusal = (refusal: ApiRefusal): string => {
  switch (refusal.kind) {
    case 'invalid-field':
      // The reason is a wire word — `too-short`, `unknown-class` — and is never
      // shown. A reason from a later version of the API that this build has
      // never heard of falls back rather than printing an identifier.
      return message(tableFor(refusal.field), refusal.reason, 'The game would not accept that. Check it and try again.');

    case 'invalid-credentials':
      /*
       * One message, and it does not lean either way.
       *
       * `apps/api` answers a wrong password and an address with no account
       * identically, having done identical work — it verifies an absent account
       * against a dummy hash so even the response *time* does not tell them
       * apart. Saying "no account with that address" here would hand back the
       * enumeration oracle that argon2 call exists to close.
       *
       * And there is nothing to offer beyond trying again: there is no password
       * reset and cannot be one until there is a mail path (`TODO.md` #390).
       */
      return 'That email or password is not right.';

    case 'email-unavailable':
      // Registration is the one endpoint that admits an address is taken, and
      // it is a deliberate, written-down trade (`TODO.md` #390) rather than
      // something to soften here.
      return 'There is already an account for that email address. Sign in instead.';

    case 'name-taken':
      // Names are unique across the realm on purpose: two nameplates a player
      // cannot tell apart is how one character impersonates another in a co-op
      // room. So this is a fact the form has to be able to state.
      return 'Another hero already carries that name. Choose another.';

    case 'too-many-characters':
      return 'This account is full. Delete a character to make room for a new one.';

    case 'no-such-character':
      return 'That character is no longer there.';

    case 'rate-limited':
      return refusal.retryAfterSeconds === null
        ? 'Too many attempts. Wait a little and try again.'
        : `Too many attempts. Try again in ${refusal.retryAfterSeconds} seconds.`;

    case 'unauthenticated':
      // Never "your token expired": the API refuses to say whether it was an
      // expiry, a forgery or an account that is gone, and a client that guessed
      // would be inventing the distinction the API spent effort removing.
      return 'You have been signed out. Sign in again to keep playing.';

    case 'unreachable':
      return 'The game server is not answering. Check your connection and try again.';

    case 'vendor-refused':
      /*
       * A refusal no form can produce, and the sentence says so rather than
       * pretending to know which exchange it was about.
       *
       * This module dresses refusals for the **sign-in and character screens**,
       * and none of their four routes goes near a vendor. The town counter has
       * its own words — `ui/vendor-model.ts`'s `describeVendorRefusal`, which
       * knows whether the answer is *sell something* or *spend something* — and
       * a second copy of them here would be the copy nobody updates. The case
       * exists because the union is exhaustive and a silent fall-through would
       * be a blank message on a form.
       */
      return 'The game would not accept that. Try again.';
  }
};
